import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { format, parseISO, startOfToday } from 'date-fns'
import { it as itLocale, enUS } from 'date-fns/locale'
import { Check, ChevronRight, CalendarIcon } from 'lucide-react'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { Calendar } from '../ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { cn } from '../../lib/utils'
import type { PlanFormData } from './plan-helpers'
import {
  WEEK_DAYS,
  type TrainingPlanDiscipline,
  type TrainingExperience,
  type WeekDay,
} from '../../types/training-plan'

interface PlanWizardProps {
  initialData: PlanFormData
  /** When inviting a coach, their display name (shown as plan context). */
  coachName?: string | null
  /** Start date and length are fixed once the plan exists (edit mode). */
  lockShape?: boolean
  submitLabel: string
  submitting: boolean
  onSubmit: (form: PlanFormData) => void
}

const STEP_KEYS = ['shape', 'schedule', 'goal', 'currentState', 'facilities', 'lifestyle'] as const

const DISCIPLINES: TrainingPlanDiscipline[] = ['bouldering', 'sport_trad']
const EXPERIENCES: TrainingExperience[] = ['beginner', 'intermediate', 'advanced']

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  )
}

export function PlanWizard({
  initialData,
  coachName,
  lockShape = false,
  submitLabel,
  submitting,
  onSubmit,
}: PlanWizardProps) {
  const { t, i18n } = useTranslation()
  const [form, setForm] = useState<PlanFormData>(initialData)
  const [step, setStep] = useState(0)
  const [calendarOpen, setCalendarOpen] = useState(false)

  const dateFnsLocale = i18n.language?.startsWith('it') ? itLocale : enUS
  const selectedDate = form.start_date ? parseISO(form.start_date) : undefined
  const startsInPast = selectedDate ? selectedDate < startOfToday() : false

  function update<K extends keyof PlanFormData>(key: K, value: PlanFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function toggleDay(day: WeekDay) {
    setForm((prev) => ({
      ...prev,
      preferred_session_days: prev.preferred_session_days.includes(day)
        ? prev.preferred_session_days.filter((d) => d !== day)
        : [...prev.preferred_session_days, day],
    }))
  }

  // Step 1 is the only one with hard requirements (start date + valid length).
  const step1Valid = form.start_date.trim() !== '' && form.length_weeks > 0
  const isLastStep = step === STEP_KEYS.length - 1
  const canAdvance = step !== 0 || step1Valid

  function next() {
    if (isLastStep) {
      if (step1Valid) onSubmit(form)
      return
    }
    setStep((s) => Math.min(s + 1, STEP_KEYS.length - 1))
  }

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-6 pb-24">
      {/* Step indicator */}
      <nav className="flex items-center justify-between" aria-label={t('trainingPlans.wizard.title')}>
        {STEP_KEYS.map((key, idx) => (
          <div key={key} className="flex items-center flex-1 last:flex-none">
            <button
              type="button"
              onClick={() => setStep(idx)}
              aria-label={t(`trainingPlans.wizard.steps.${key}`)}
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors',
                idx === step ? 'border-primary text-primary' : 'border-muted text-muted-foreground'
              )}
            >
              {idx < step ? <Check className="size-4" /> : idx + 1}
            </button>
            {idx < STEP_KEYS.length - 1 && <div className="h-0.5 flex-1 bg-muted mx-1" />}
          </div>
        ))}
      </nav>

      <h2 className="text-lg font-semibold">{t(`trainingPlans.wizard.steps.${STEP_KEYS[step]}`)}</h2>

      {coachName && (
        <p className="text-sm text-muted-foreground -mt-3">
          {t('trainingPlans.wizard.coachContext', { coach: coachName })}
        </p>
      )}

      <div className="flex flex-col gap-4">
        {step === 0 && (
          <>
            <Field label={t('trainingPlans.wizard.fields.startDate')}>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={lockShape}
                    className={cn(
                      'justify-start text-left font-normal',
                      !selectedDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 size-4" />
                    {selectedDate
                      ? format(selectedDate, 'PPP', { locale: dateFnsLocale })
                      : t('common.pickDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => {
                      if (d) update('start_date', format(d, 'yyyy-MM-dd'))
                      setCalendarOpen(false)
                    }}
                    defaultMonth={selectedDate}
                    autoFocus
                  />
                </PopoverContent>
              </Popover>
              {lockShape && (
                <p className="text-xs text-muted-foreground">{t('trainingPlans.wizard.lockedHint')}</p>
              )}
              {startsInPast && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {t('trainingPlans.wizard.pastDateWarning')}
                </p>
              )}
            </Field>

            <Field label={t('trainingPlans.wizard.fields.discipline')}>
              <Select
                value={form.discipline}
                onValueChange={(v) => update('discipline', v as TrainingPlanDiscipline)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISCIPLINES.map((d) => (
                    <SelectItem key={d} value={d}>{t(`trainingPlans.discipline.${d}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label={t('trainingPlans.wizard.fields.experience')}>
              <Select
                value={form.training_experience}
                onValueChange={(v) => update('training_experience', v as TrainingExperience)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EXPERIENCES.map((x) => (
                    <SelectItem key={x} value={x}>{t(`trainingPlans.experience.${x}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label={t('trainingPlans.wizard.fields.lengthWeeks')}>
                <Input
                  type="number"
                  min={1}
                  value={form.length_weeks}
                  disabled={lockShape}
                  onChange={(e) => update('length_weeks', Math.max(1, Number(e.target.value) || 0))}
                />
              </Field>
              <Field label={t('trainingPlans.wizard.fields.sessionDuration')}>
                <Input
                  type="number"
                  min={0}
                  value={form.session_duration_minutes}
                  onChange={(e) =>
                    update('session_duration_minutes', e.target.value === '' ? '' : Number(e.target.value))
                  }
                />
              </Field>
            </div>

            {!step1Valid && (
              <p className="text-xs text-destructive">{t('trainingPlans.wizard.validation.lengthWeeks')}</p>
            )}
          </>
        )}

        {step === 1 && (
          <>
            <Field label={t('trainingPlans.wizard.fields.sessionsPerWeek')}>
              <Input
                type="number"
                min={0}
                value={form.sessions_per_week}
                onChange={(e) =>
                  update('sessions_per_week', e.target.value === '' ? '' : Number(e.target.value))
                }
              />
            </Field>
            <Field label={t('trainingPlans.wizard.fields.preferredDays')}>
              <div className="flex flex-wrap gap-1.5">
                {WEEK_DAYS.map((day) => {
                  const active = form.preferred_session_days.includes(day)
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-sm transition-colors',
                        active
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:bg-muted'
                      )}
                    >
                      {t(`trainingPlans.days.${day}`)}
                    </button>
                  )
                })}
              </div>
            </Field>
          </>
        )}

        {step === 2 && (
          <Field label={t('trainingPlans.wizard.fields.goal')}>
            <Textarea
              value={form.goal}
              onChange={(e) => update('goal', e.target.value)}
              placeholder={t('trainingPlans.wizard.fields.goalPlaceholder')}
              rows={5}
            />
          </Field>
        )}

        {step === 3 && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t('trainingPlans.wizard.fields.typicalGrade')}>
                <Input value={form.typical_grade} onChange={(e) => update('typical_grade', e.target.value)} />
              </Field>
              <Field label={t('trainingPlans.wizard.fields.bestGrade')}>
                <Input value={form.best_grade} onChange={(e) => update('best_grade', e.target.value)} />
              </Field>
            </div>
            <Field label={t('trainingPlans.wizard.fields.styleStrengths')}>
              <Textarea value={form.style_strengths} onChange={(e) => update('style_strengths', e.target.value)} rows={2} />
            </Field>
            <Field label={t('trainingPlans.wizard.fields.styleLimiters')}>
              <Textarea value={form.style_limiters} onChange={(e) => update('style_limiters', e.target.value)} rows={2} />
            </Field>
            <Field label={t('trainingPlans.wizard.fields.recentTraining')}>
              <Textarea value={form.recent_training} onChange={(e) => update('recent_training', e.target.value)} rows={3} />
            </Field>
          </>
        )}

        {step === 4 && (
          <Field label={t('trainingPlans.wizard.fields.facilities')}>
            <Textarea value={form.facilities} onChange={(e) => update('facilities', e.target.value)} rows={5} />
          </Field>
        )}

        {step === 5 && (
          <>
            <Field label={t('trainingPlans.wizard.fields.workDemands')}>
              <Textarea value={form.work_demands} onChange={(e) => update('work_demands', e.target.value)} rows={2} />
            </Field>
            <Field label={t('trainingPlans.wizard.fields.otherActivity')}>
              <Textarea value={form.other_activity} onChange={(e) => update('other_activity', e.target.value)} rows={2} />
            </Field>
            <Field label={t('trainingPlans.wizard.fields.injuriesConstraints')}>
              <Textarea value={form.injuries_constraints} onChange={(e) => update('injuries_constraints', e.target.value)} rows={2} />
            </Field>
            <Field label={t('trainingPlans.wizard.fields.preferences')}>
              <Textarea value={form.preferences} onChange={(e) => update('preferences', e.target.value)} rows={2} />
            </Field>
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 pt-2">
        <Button type="button" variant="outline" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
          {t('trainingPlans.wizard.prev')}
        </Button>
        <Button type="button" disabled={!canAdvance || submitting} onClick={next}>
          {isLastStep ? (submitting ? t('common.saving') : submitLabel) : t('trainingPlans.wizard.next')}
          {!isLastStep && <ChevronRight className="size-4" />}
        </Button>
      </div>
    </div>
  )
}
